import { memo, useCallback, useMemo } from 'react';
import { isString } from 'radash';
import ClipCustomizerAspectRatioOption from './ClipCustomizerAspectRatioOption';
import ClipCustomizerMagicLayoutOption from './ClipCustomizerMagicLayoutOption';
import MatchSpeakersCTA from './MatchSpeakersCTA';
import useClipCustomiserLayoutPopup from './useClipLayoutPopupOptions';
import ClipCustomizerAspectRatioTooltip from './ClipCustomizerAspectRatioTooltip';
import { ClipToggleOptions } from './ClipToggleOptions';
import ClipCustomizerMultipleSceneWarning from './ClipCustomizerMultipleSceneWarning';
import { CLIP_CUSTOMIZER_WARNING } from '../../ClipCustomizer/useClipCustomizerWarnings';
import ClipCustomizerMigrateNudge from './ClipCustomizerMigrateNudge';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { toggleHideBorders, toggleSpeakerLabels, isLayoutsWithVisionDataEnabled } from '@/stores/clip';
import {
  canUseNewTimelineForUnmigratedLiveEvent,
  doesClipWorkOnAlignedStreams,
  isClipAudiogram,
  isClipLayoutDefault,
  isFitAppliedForClip,
  isOriginalLayoutAvailable
} from '@/player-config/src/utils/clipUtils';
import { isLayoutAudiogram } from '@/player-config/src/utils/layout';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';

function ClipCustomizerLayoutPopup({ warnings }: Pick<CLIP_CUSTOMIZER_WARNING, 'warnings'>) {
  const { clipId, clipData } = useClipsContext();

  const onNameCardToggle = useCallback(
    (value: boolean) => {
      toggleSpeakerLabels(clipId, value);
    },
    [clipId]
  );

  const onBorderToggle = useCallback(
    (value: boolean) => {
      toggleHideBorders(clipId, !value);
    },
    [clipId]
  );

  const isOriginalAvailable = useMemo(() => {
    return isOriginalLayoutAvailable(clipData);
  }, [clipData]);

  const isSpeakerLayoutAvailable = useMemo(() => {
    if (
      doesClipWorkOnAlignedStreams(clipData) ||
      isLayoutsWithVisionDataEnabled(clipData) ||
      canUseNewTimelineForUnmigratedLiveEvent(clipData)
    ) {
      return true;
    }
    return false;
  }, [clipData]);

  const { isBorderDisabled } = useMemo(() => {
    const clipLayout = clipData.asset_metadata.layout;
    const _isLayoutAudiogram = isLayoutAudiogram(clipLayout);

    const isBorderDisabled = _isLayoutAudiogram;

    return { isBorderDisabled };
  }, [clipData.asset_metadata.layout]);

  const { showNameCard, isSpeakerLabelOn } = useMemo(() => {
    const sceneCount = clipData.asset_metadata.scenes?.length ?? 0;
    const hideNameCard = !(sceneCount > 1) && isClipLayoutDefault(clipData);
    return {
      showNameCard: !hideNameCard, // if there is only one scene then clip layout and scene layout are in sync
      isSpeakerLabelOn: !!clipData.asset_metadata.magicLayout?.showSpeakerLabels || isClipAudiogram(clipData)
    };
  }, [clipData]);

  const triggerSpeakerIdentification = useCallback((params = {}) => {
    EventBus.dispatch(CustomEvents.OpenSpeakersIdentification, params);
  }, []);

  const {
    hoveringAspectRatio,
    onHoverAspectRatio,
    showMatchSpeakersCTA,
    originalLayoutDisableReason,
    gridDisableReason,
    focusDisableReason,
    pipDisableReason,
    fullScreenDisableReason,
    fitFillDisabledReason
  } = useClipCustomiserLayoutPopup();

  const isFitApplied = useMemo(() => {
    return isFitAppliedForClip(clipData);
  }, [clipData]);

  const disabledSpeakerLabelToggle = useMemo(() => {
    if (isClipAudiogram(clipData)) {
      return true;
    }
    return false;
  }, [clipData]);

  return (
    <div className="flex max-h-[60vh] w-96 flex-col overflow-y-auto rounded-lg border bg-white p-4 shadow">
      <div className="font-medium">Aspect Ratio</div>
      <div className="mt-2 rounded-md bg-slate-200 p-0.5">
        <div data-tooltip-id="aspect-ratio-tooltip" className="grid grid-cols-3 items-center gap-0.5">
          <ClipCustomizerAspectRatioOption onHover={onHoverAspectRatio} sizeOption="LANDSCAPE" />
          <ClipCustomizerAspectRatioOption onHover={onHoverAspectRatio} sizeOption="SQUARE" />
          <ClipCustomizerAspectRatioOption onHover={onHoverAspectRatio} sizeOption="PORTRAIT" />
        </div>
      </div>
      <ClipCustomizerMigrateNudge />
      <ClipCustomizerMultipleSceneWarning />
      <div className="mt-5 font-medium">Layout</div>
      <div className="mt-3 flex flex-col gap-y-1.5">
        {isOriginalAvailable && (
          <ClipCustomizerMagicLayoutOption
            magicLayoutOption="DEFAULT"
            disabled={isString(originalLayoutDisableReason)}
            disabledReason={originalLayoutDisableReason ?? ''}
          />
        )}
        {isSpeakerLayoutAvailable ? (
          <>
            <ClipCustomizerMagicLayoutOption
              magicLayoutOption="SPEAKER"
              disabled={isString(fullScreenDisableReason)}
              disabledReason={fullScreenDisableReason ?? ''}
            />
            <ClipCustomizerMagicLayoutOption
              magicLayoutOption="GRID"
              disabled={isString(gridDisableReason)}
              disabledReason={gridDisableReason ?? ''}
            />
            <ClipCustomizerMagicLayoutOption
              magicLayoutOption="FOCUS"
              disabled={isString(focusDisableReason)}
              disabledReason={focusDisableReason ?? ''}
            />
            <ClipCustomizerMagicLayoutOption
              magicLayoutOption="PIP"
              disabled={isString(pipDisableReason)}
              disabledReason={pipDisableReason ?? ''}
            />
          </>
        ) : (
          <></>
        )}
        <ClipCustomizerMagicLayoutOption magicLayoutOption="AUDIOGRAM" disabled={false} disabledReason={''} />
      </div>
      {showMatchSpeakersCTA && (
        <MatchSpeakersCTA
          onClick={() =>
            triggerSpeakerIdentification({
              isInlineIdentification: true,
              layout: clipData.asset_metadata.layout
            })
          }
          warnings={warnings}
        />
      )}
      <ClipToggleOptions
        showNameCard={showNameCard}
        isSpeakerLabelOn={isSpeakerLabelOn}
        onNameCardToggle={onNameCardToggle}
        showBorder={!clipData.asset_metadata.hide_borders}
        onBorderToggle={onBorderToggle}
        isBorderDisabled={isBorderDisabled}
        isFitApplied={isFitApplied}
        fitFillDisabledReason={fitFillDisabledReason}
        disabledSpeakerLabelToggle={disabledSpeakerLabelToggle}
      />
      <ClipCustomizerAspectRatioTooltip hoveringAspectRatio={hoveringAspectRatio} />
    </div>
  );
}

export default memo(ClipCustomizerLayoutPopup);
